import React from 'react'
import {PayPalScriptProvider, PayPalButtons} from '@paypal/react-paypal-js'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {MiniCartPreferences, OrderAddressModel, PaymentInstrumentModel, BasketModel} from '_types'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShippingMethodsForShipment, useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useBasket, useCustomApi, useOrder, useAnalytics} from '_customHooks'
import {useLoader} from '_context'

interface PayPalButtonWrapperProps {
    sitePreference?: MiniCartPreferences
    basketId?: string
    currency?: string
    subTotal?: number
    allOrderable?: boolean
}

/**
 * @name PayPalButtonWrapper
 *
 * @component
 *
 * @description
 * Renders PayPal button.
 *
 * @param {MiniCartPreferences} props.sitePreference - basket object
 * @param {string} props.basketId - basket ID
 * @param {currency} props.currency - basket currency
 * @param {number} props.subTotal - basket subTotal
 *
 * @returns {JSX.Element} Rendered `PayPalButtonWrapper` component.
 */
const PayPalButtonWrapper = ({sitePreference, basketId, currency, subTotal, allOrderable}: PayPalButtonWrapperProps) => {
    const [localBasketId] = React.useState(basketId)
    const [localCurrency] = React.useState(currency)
    const {data: customer} = useCurrentCustomer()
    const {showLoader, hideLoader} = useLoader()
    const {usePlaceOrder, useCalculateTaxesAvalara, useFailOrder} = useCustomApi()
    const {analyticsPaypalExpressStart} = useAnalytics()
    const {mutateAsync: calculateTaxesMutation} = useCalculateTaxesAvalara()
    const {locale, site} = useMultiSite()
    const placeOrderMutation = usePlaceOrder()
    const failOrder = useFailOrder()
    let orderID = ''
    const {
        updateBasketCustomer,
        updateBasketShippingAddress,
        updateBasketShippingMethod,
        addPaymentInstrumentToBasket,
        refetchBasket,
        removePaymentInstrument,
    } = useBasket(false)
    const {createOrder, updatePaymentInstrument} = useOrder(false)
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {data: shippingMethods} = useShippingMethodsForShipment({
        parameters: {
            basketId: basketId || '',
            shipmentId: 'me',
        },
    })

    const intent = sitePreference?.PP_API_PaymentAction ? 'capture' : 'authorize'
    const showPaypalButton =
        !!sitePreference &&
        !!localCurrency &&
        sitePreference.PAYPAL_EXPRESS_Allowed_Currencies?.includes(localCurrency) &&
        sitePreference.PP_API_Button_Location?.[0]?.indexOf('Minicart') !== -1

    const shouldCalculateTaxes = sitePreference?.Allowed_Suretax_Dispatch

    const splitFullName = (fullName?: string) => {
        if (!fullName) {
            return {
                firstName: '',
                lastName: '',
            }
        }

        const [firstName, ...rest] = fullName.trim().split(' ')
        return {
            firstName: firstName || '',
            lastName: rest.join(' ') || '',
        }
    }

    const updateBasketAndCreateOrder = async (paypalOrder) => {
        const payer = paypalOrder.payer
        const paypalEmail = payer.email_address
        const purchaseUnit = paypalOrder.purchase_units[0]
        const shipping = purchaseUnit.shipping
        try {
            // aggiornamento email del basket
            const email = customer.isGuest ? paypalEmail : customer.email
            await updateBasketCustomer({email})

            // aggiornamento del metodo di spedizione del basket
            let shippingMethodId = ''
            if (shipping.address.admin_area_1 === 'NIR' && shipping.address.country_code === 'GB') {
                shippingMethodId = sitePreference?.ShippingMethod_NI || ''
            } else {
                const defaultId = shippingMethods?.defaultShippingMethodId
                const applicable = shippingMethods?.applicableShippingMethods || []
                const foundDefault = applicable.find((method) => method.id === defaultId)
                if (foundDefault && defaultId) {
                    shippingMethodId = defaultId
                } else {
                    // Prendi il primo metodo con c_shipToStoreEnabled e c_storePickupEnabled a false
                    const fallback = applicable.find((method) => method.c_shipToStoreEnabled === false && method.c_storePickupEnabled === false)
                    if (fallback) {
                        shippingMethodId = fallback.id
                    }
                }
            }
            await updateBasketShippingMethod(shippingMethodId)

            // aggiornamento indirizzo di spedizione del basket sarà lo stesso del billing

            const {firstName, lastName} = splitFullName(shipping.name?.full_name)

            const shippingBody: OrderAddressModel = {
                address1: shipping.address.address_line_1,
                address2: shipping.address.address_line_2,
                city: shipping.address.admin_area_2,
                countryCode: shipping.address.country_code,
                firstName: firstName || payer.name.given_name,
                lastName: lastName || `${payer.name.middle_name || ''} ${payer.name.surname}`.trim(),
                phone: payer.phone.phone_number.national_number,
                postalCode: shipping.address.postal_code,
                stateCode: shipping.address.admin_area_1,
                c_titleSelect: !customer.isGuest && customer.c_titleSelect ? customer.c_titleSelect : '',
            }
            const updatedBasket: BasketModel = await updateBasketShippingAddress(shippingBody, true)
            const existingPaymentInstruments = updatedBasket?.paymentInstruments || []

            // rimuovo tutti i payment instruments esistenti
            for (const instrument of existingPaymentInstruments) {
                if (instrument.paymentInstrumentId) {
                    await removePaymentInstrument(instrument.paymentInstrumentId)
                }
            }

            // aggiornamento del metodo di pagamento del basket
            const paymentInstrument: PaymentInstrumentModel = {
                paymentMethodId: 'PayPal',
                amount: subTotal,
            }

            await addPaymentInstrumentToBasket(paymentInstrument)

            const order = await createOrder(localBasketId)
            return {
                error: false,
                order: order,
            }
        } catch (e) {
            return {
                error: true,
                message: e || 'An error occurred while updating the basket and creating the order.',
            }
        }
    }

    const onApprove = async (data, actions) => {
        showLoader()
        try {
            const paypalOrder = await actions.order.get()
            const responseObj = await updateBasketAndCreateOrder(paypalOrder)
            const order = responseObj.order
            orderID = order?.orderNo || ''

            if (!order || !order.orderNo) {
                hideLoader()
                return actions.reject()
            }
            const hostname = window?.location?.hostname
            const host = hostname === 'localhost' ? 'localhost:3000' : hostname
            const headers = {
                'Content-Type': 'application/json',
            }
            const capture = await fetch(`${window.location.protocol}//${host}/paypalCapture?orderID=${data.orderID}&siteId=${site.id}`, {
                method: 'POST',
                headers,
            })

            const captureResponseJson = await capture.json()
            if (captureResponseJson && !captureResponseJson.error && captureResponseJson.data) {
                const captureResponse = captureResponseJson.data
                const paypalEmail = captureResponse.payer.email_address
                const paymentInstrument = order?.paymentInstruments
                const capturedTotal = Number(captureResponse.purchase_units[0].payments.captures[0].amount.value)

                if (paymentInstrument && paymentInstrument.length > 0 && paymentInstrument[0].paymentInstrumentId) {
                    const paymentInstrumentObj = {
                        amount: capturedTotal,
                        paymentMethodId: 'PayPal',
                        c_currentPaypalEmail: paypalEmail,
                        c_paypalOrderID: captureResponse.id,
                        c_paypalPaymentStatus: captureResponse.status,
                        c_paypalTransactionId: captureResponse.purchase_units[0].payments.captures[0].id,
                    }
                    await updatePaymentInstrument(order.orderNo, paymentInstrument[0].paymentInstrumentId, paymentInstrumentObj)
                }

                const placeOrderResponse = await placeOrderMutation.mutateAsync({locale: locale.id, orderId: order.orderNo})

                window.location.href = placeOrderResponse.continueUrl
                hideLoader()
                return captureResponse
            } else {
                hideLoader()
                await failOrder.mutateAsync({locale: locale.id, orderId: order.orderNo})
                await refetchBasket()
                return actions.reject()
            }
        } catch (error) {
            console.error(error)
            hideLoader()
            if (orderID !== '') {
                await failOrder.mutateAsync({locale: locale.id, orderId: orderID})
                await refetchBasket()
            }
            return actions.reject()
        }
    }

    const onCreateOrder = async (data, actions) => {
        const body = {
            application_context: {
                cancel_url: `${window.location.origin}/${locale.alias}/cart`,
                locale: locale.id,
            },
            purchase_units: [
                {
                    amount: {
                        currency_code: localCurrency,
                        value: subTotal?.toString(),
                    },
                    shipping: null,
                    breakdown: {
                        item_total: {
                            currency_code: localCurrency,
                            value: subTotal?.toString(),
                        },
                    },
                },
            ],
            intent: intent?.toUpperCase(),
            payment_source: {
                paypal: {
                    experience_context: {
                        shipping_preference: 'GET_FROM_FILE',
                    },
                    attributes: {},
                },
            },
        }

        return await actions.order.create(body)
    }

    const openPayPal = (data, actions) => {
        // Check if the basket is valid before proceeding
        if (subTotal === 0) {
            return actions.reject()
        }
        analyticsPaypalExpressStart({accessType: 'minicart'})
    }

    const onShippingAddressChange = async (data, actions) => {
        if (data.shippingAddress.countryCode !== locale.id.split('-')[1].toUpperCase()) {
            return actions.reject(data.errors.COUNTRY_ERROR)
        }
        const shipping = data.shippingAddress
        if (shouldCalculateTaxes) {
            const token = await getTokenWhenReady()
            const shippingBody: OrderAddressModel = {
                city: shipping.city,
                countryCode: shipping.countryCode,
                postalCode: shipping.postalCode,
                stateCode: shipping.state,
            }
            const headersCommerce = {
                authorization: `Bearer ${token}`,
            }
            await api.shopperBaskets.updateShippingAddressForShipment({
                headers: headersCommerce,
                parameters: {
                    basketId: basketId || '',
                    shipmentId: 'me',
                },
                body: shippingBody,
            })
            // await updateBasketShippingAddress(shippingBody, true)
            const taxesResponse = await calculateTaxesMutation({basketId: basketId || '', applyTaxesToBasket: true})
            const newTotal = (subTotal + (taxesResponse?.totalTax || 0)).toFixed(2)
            const patchBody = [
                {
                    op: 'replace',
                    path: "/purchase_units/@reference_id=='default'/amount",
                    value: {
                        currency_code: localCurrency,
                        value: newTotal.toString(),
                        breakdown: {
                            item_total: {
                                currency_code: localCurrency,
                                value: subTotal?.toString(),
                            },
                            shipping: {
                                currency_code: localCurrency,
                                value: taxesResponse?.totalTax.toString(),
                            },
                        },
                    },
                },
            ]

            const hostname = window?.location?.hostname
            const host = hostname === 'localhost' ? 'localhost:3000' : hostname
            const headers = {
                'Content-Type': 'application/json',
            }
            return fetch(`${window.location.protocol}//${host}/paypalUpdate?orderID=${data.orderID}&siteId=${site.id}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(patchBody),
            })
        }

        return
    }

    return showPaypalButton && sitePreference.paypal_clientId && sitePreference.paypal_button_style ? (
        <PayPalScriptProvider
            options={{
                clientId: sitePreference.paypal_clientId,
                currency: localCurrency,
                intent: intent,
                disableFunding: (sitePreference.PAYPAL_EXPRESS_Disable_Funds || []).join(','),
                components: 'buttons',
            }}
        >
            <PayPalButtons
                style={JSON.parse(sitePreference.paypal_button_style)}
                className={'paypal_container'}
                onApprove={onApprove}
                createOrder={onCreateOrder}
                onClick={openPayPal}
                onShippingAddressChange={onShippingAddressChange}
                disabled={subTotal === 0 || !allOrderable}
            />
        </PayPalScriptProvider>
    ) : (
        <></>
    )
}

export {PayPalButtonWrapper}
